import { useTranslation } from 'react-i18next'
import { useStatusOverlay } from '@fs/zion-ui'
import { useEffect } from 'react'
import { usePlacesAppState } from '../PlacesApp/PlacesAppContext'

type Health = {
  canRead?: boolean
  canWrite?: boolean
  [key: string]: unknown
}

type Permissions = {
  PERM_ATTRIBUTE_MODIFY?: boolean
  [key: string]: unknown
}

type TFunction = (key: string, defaultValue: string) => string

function getStatusMessage({
  permissions,
  health,
  t,
}: {
  permissions?: Permissions | null
  health?: Health | null
  t: TFunction
}): string | undefined {
  const cannotRead = !health?.canRead
  const hasPermissionButCannotWrite = Boolean(permissions?.PERM_ATTRIBUTE_MODIFY && !health?.canWrite)
  if (health) {
    if (cannotRead) {
      return t(
        'places-app.read-system-not-available.banner',
        "We're sorry. The system is temporarily unavailable. Please try again later."
      )
    }
    if (hasPermissionButCannotWrite) {
      return t(
        'places-app.write-system-not-available.banner',
        'The system is temporarily unavailable for editing. Your connection has been put into read-only mode.'
      )
    }
  }
  return undefined
}

export default function usePlaceSystemHealthStatusOverlay(): void {
  const placesAppState = usePlacesAppState()
  const { health, permissions } = placesAppState
  const [t] = useTranslation()
  const statusOverlay = useStatusOverlay()
  const message = getStatusMessage({ permissions, health, t })

  useEffect(() => {
    if (message) {
      statusOverlay({
        message,
        type: 'warning',
        dismissable: 'dismiss',
      })
    }
  }, [message, statusOverlay])
}
